import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useMeta from '../hooks/useMeta';
import { metaApi, reportsApi, combinedApi } from '../services/api';
import ReportVisualization from '../components/reports/ReportVisualization';
import ClinicDataSection from '../components/ClinicDataSection';
import toast from 'react-hot-toast';

const conversionOptions = [
  { value: 'lead', label: 'Leads Cadastros' },
  { value: 'message', label: 'Conversas Iniciadas' },
  { value: 'click', label: 'Cliques' },
  { value: 'conversion', label: 'Conversões (Gerais)' },
  { value: 'purchase', label: 'Compras' }
];

function extractTargetMetric(rawData, convType) {
  if (!rawData || !Array.isArray(rawData)) return 0;
  let total = 0;
  rawData.forEach(item => {
    let metric = 0;
    if (item.actions && Array.isArray(item.actions)) {
      item.actions.forEach(action => {
        const type = action.action_type;
        const val = parseInt(action.value, 10) || 0;
        if (convType === 'lead') {
          if (type === 'lead' || type === 'onsite_conversion.lead_grouped' || type.includes('lead')) metric += val;
        } else if (convType === 'message') {
          if (type === 'onsite_conversion.messaging_conversation_started_7d' || type === 'contact') metric += val;
        }
      });
    }
    total += metric;
  });
  return total;
}

const Reports = () => {
  const { accounts, fetchAccounts } = useMeta();
  const location = useLocation();
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  
  // Date and Range selection
  const [dateSelectionType, setDateSelectionType] = useState('preset'); // 'preset' | 'custom'
  const [datePreset, setDatePreset] = useState('last_7d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Granularity Level — 'geral' (default) unifies both: criativos section on top,
  // campanhas section below. 'campaign' / 'ad' show a single section as before.
  const [level, setLevel] = useState('geral'); // 'geral' | 'campaign' | 'ad'
  
  // Conversion Metric Focus
  const [conversionType, setConversionType] = useState('message');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [previousReportData, setPreviousReportData] = useState([]);
  const [secondaryReportData, setSecondaryReportData] = useState([]); // ad-level rows, only used when level === 'geral'
  const [demographicsData, setDemographicsData] = useState(null);
  const [campaignSummary, setCampaignSummary] = useState(null);

  // Lead Goal State (Funil Projetado)
  const [showFunnelAndGoals, setShowFunnelAndGoals] = useState(true);
  const [leadGoal, setLeadGoal] = useState(null);
  const [monthAchieved, setMonthAchieved] = useState(0);
  const [leadGoalLegend, setLeadGoalLegend] = useState('');
  const reportRequestIdRef = React.useRef(0);

  // Sharing State
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareExpires, setShareExpires] = useState(72);
  const [sharePermanent, setSharePermanent] = useState(false);
  const [shareLabel, setShareLabel] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Preselect account from URL query string if provided
  useEffect(() => {
    if (accounts.length > 0) {
      const queryParams = new URLSearchParams(location.search);
      const accId = queryParams.get('account_id');
      if (accId) {
        // Validate if account_id is valid
        const exists = accounts.some(acc => {
           const id = acc.account_id || acc.metaAccountId || (acc.id.startsWith('act_') ? acc.id.replace('act_', '') : acc.id);
           return id === accId;
        });
        if (exists) {
           setSelectedAccount(accId);
        }
      }
    }
  }, [accounts, location.search]);

  const computeDemographicDates = () => {
    const today = new Date();
    const fmt = d => d.toISOString().split('T')[0];
    if (dateSelectionType === 'custom' || dateSelectionType === 'current_month') {
      return { start: startDate, end: endDate };
    }
    const presetDays = { last_7d: 7, last_14d: 14, last_30d: 30, lifetime: 90 };
    const days = presetDays[datePreset] || 30;
    const start = new Date(today);
    start.setDate(start.getDate() - days);
    return { start: fmt(start), end: fmt(today) };
  };

  const handleGenerateReport = async () => {
    if (!selectedAccount) {
      toast.error('Por favor, selecione uma conta');
      return;
    }
    if (dateSelectionType === 'custom' && (!startDate || !endDate)) {
      toast.error('Por favor, selecione um período');
      return;
    }

    const currentRequestId = ++reportRequestIdRef.current;
    setIsLoading(true);
    setDemographicsData(null);
    setCampaignSummary(null);
    setSecondaryReportData([]);
    setLeadGoal(null);
    setMonthAchieved(0);
    setLeadGoalLegend('');
    try {
      // 'geral' isn't a real Meta API level — it's campaign-level data as the
      // primary dataset, plus a second ad-level fetch for the creatives section.
      const isGeral = level === 'geral';
      const primaryLevel = isGeral ? 'campaign' : level;
      const params = { level: primaryLevel };
      if (dateSelectionType === 'preset') {
        params.datePreset = datePreset;
      } else {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      const response = await metaApi.getInsights(selectedAccount, params);
      if (currentRequestId !== reportRequestIdRef.current) return;

      if (response && response.data) {
        setReportData(response.data);
        setPreviousReportData(response.previousData || []);
        setIsVisualizing(true);
        toast.success('Dados do relatório carregados com sucesso');

        // Fetch lead goal and current month achieved (for message or lead conversions)
        if (showFunnelAndGoals && (conversionType === 'message' || conversionType === 'lead')) {
          const now = new Date();
          const curYear = now.getFullYear();
          const curMonth = now.getMonth() + 1;
          const curDay = now.getDate();
          const localMonthStr = String(curMonth).padStart(2, '0');
          const localDayStr = String(curDay).padStart(2, '0');
          const mNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
          const mShort = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
          const calcLegend = `Mês de ${mNames[curMonth - 1]}/${curYear} (1-${curDay} ${mShort[curMonth - 1]}), independente do período selecionado`;
          setLeadGoalLegend(calcLegend);
          setLeadGoal({ state: 'loading' });

          const startOfMonthStr = `${curYear}-${localMonthStr}-01`;
          const todayStr = `${curYear}-${localMonthStr}-${localDayStr}`;

          Promise.all([
            reportsApi.getLeadGoal(selectedAccount, curYear, curMonth),
            conversionType === 'message'
              ? metaApi.getInsights(selectedAccount, {
                  startDate: startOfMonthStr,
                  endDate: todayStr,
                  level: 'campaign'
                })
              : Promise.resolve({ data: [] })
          ]).then(([gRes, mRes]) => {
            if (currentRequestId !== reportRequestIdRef.current) return;

            if (!gRes || !gRes.data) {
              setLeadGoal({ state: 'error' });
              return;
            }

            const goalData = gRes.data;
            if (goalData.state === 'unmapped' || goalData.state === 'multiple' || goalData.state === 'no_goal') {
              setLeadGoal(goalData);
              return;
            }

            if (conversionType === 'lead') {
              const clinicLeads = Number(goalData.clinicLeadsMonth || 0);
              if (clinicLeads <= 0) {
                // Se esse total for 0 (sem dados de clinica no mes), NAO exibir o card (nao mostrar 0% vermelho)
                setLeadGoal(null);
                setMonthAchieved(0);
                return;
              }
              setMonthAchieved(clinicLeads);
              setLeadGoal(goalData);
              return;
            }

            // conversionType === 'message'
            if (!mRes || !Array.isArray(mRes.data)) {
              // If month insights failed, mark error and do not allow 0%
              setLeadGoal({ state: 'error' });
              return;
            }

            const totalAchieved = extractTargetMetric(mRes.data, conversionType);
            setMonthAchieved(totalAchieved);
            setLeadGoal(goalData);
          }).catch(err => {
            if (currentRequestId !== reportRequestIdRef.current) return;
            console.warn('Falha ao carregar meta e realizado do mês:', err);
            setLeadGoal({ state: 'error' });
          });
        }

        // When at ad level, also fetch campaign totals for KPI header (non-blocking)
        if (level === 'ad') {
          const campaignParams = { ...params, level: 'campaign' };
          metaApi.getInsights(selectedAccount, campaignParams)
            .then(cRes => {
              if (currentRequestId !== reportRequestIdRef.current) return;
              if (cRes?.data) setCampaignSummary(cRes.data);
            })
            .catch(() => {});
        }

        // Nível 'geral': fetch ad-level rows too, for the creatives section
        if (isGeral) {
          metaApi.getInsights(selectedAccount, { ...params, level: 'ad' })
            .then(adRes => {
              if (currentRequestId !== reportRequestIdRef.current) return;
              if (adRes?.data) setSecondaryReportData(adRes.data);
            })
            .catch(() => {});
        }

        // Fetch demographics in background (non-blocking)
        const { start, end } = computeDemographicDates();
        combinedApi.getDemographics(selectedAccount, start, end)
          .then(demoRes => {
            if (currentRequestId !== reportRequestIdRef.current) return;
            if (demoRes?.data) setDemographicsData(demoRes.data);
          })
          .catch(() => {}); // Silently ignore demographic errors
      } else {
        toast.error('Nenhum dado encontrado para este período');
      }
    } catch (error) {
      if (currentRequestId !== reportRequestIdRef.current) return;
      console.error('Falha ao gerar o relatório:', error);
      toast.error(error.message || 'Falha ao gerar o relatório');
    } finally {
      if (currentRequestId === reportRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleBackToOptions = () => {
    reportRequestIdRef.current++;
    setIsVisualizing(false);
    setDemographicsData(null);
    setCampaignSummary(null);
    setSecondaryReportData([]);
    setLeadGoal(null);
    setMonthAchieved(0);
    setLeadGoalLegend('');
  };

  const handleExportCsv = (processedData) => {
    if (!processedData || processedData.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }

    const isAdLevel = level === 'ad';
    const convLabel = conversionOptions.find(o => o.value === conversionType)?.label || 'Conversões';
    
    // Default headers
    const headers = [
      isAdLevel ? 'Nome do Anúncio' : 'Campanha', 
      'Gasto', 'Impressões', 'Cliques no Link', 'CTR (%)', 'CPC (R$)', 
      convLabel, 'Custo por Ação (R$)'
    ];
    
    // CSV Rows
    const rows = processedData.map(row => [
      `"${isAdLevel ? (row.ad_name || 'Desconhecido') : (row.campaign_name || 'Desconhecido')}"`,
      row.spend,
      row.impressions,
      row.clicks,
      row.ctr,
      row.cpc,
      row.targetMetric,
      row.cpaTarget
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    
    const fileSuffix = dateSelectionType === 'preset' ? datePreset : `${startDate}_to_${endDate}`;
    link.setAttribute("download", `relatorio_${level}_${conversionType}_${fileSuffix}.csv`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Relatório exportado como CSV');
  };

  const generateShareLink = async () => {
    setIsSharing(true);
    setShareLink('');
    try {
      const selectedAccObj = accounts.find(a => {
        const id = a.account_id || a.metaAccountId || (a.id.startsWith('act_') ? a.id.replace('act_', '') : a.id);
        return id === selectedAccount;
      });
      let leadGoalSnapshot = null;
      const isValidGoal = leadGoal && leadGoal.state === 'ok' && (
        conversionType === 'message' || (conversionType === 'lead' && monthAchieved > 0)
      );

      if (isValidGoal) {
        const now = new Date();
        const curYear = now.getFullYear();
        const curMonth = now.getMonth() + 1;
        const curDay = now.getDate();
        const totalDays = new Date(curYear, curMonth, 0).getDate();
        const pacing = Number(((Math.min(curDay, totalDays) / totalDays) * 100).toFixed(1));

        leadGoalSnapshot = {
          goal: {
            ...leadGoal,
            currentDay: curDay,
            totalDaysInMonth: totalDays,
            expectedPacingPct: pacing
          },
          achieved: monthAchieved,
          conversionType,
          legend: leadGoalLegend
        };
      }

      const data = {
        reportType: 'ad_account',
        entityId: selectedAccount,
        entityName: selectedAccObj ? selectedAccObj.name : 'Conta Desconhecida',
        adAccountId: selectedAccObj?.id || null,
        ttlHours: parseInt(shareExpires),
        permanent: sharePermanent,
        label: shareLabel.trim() || null,
        options: {
          level,
          conversionType,
          dateSelectionType,
          datePreset: dateSelectionType === 'preset' ? datePreset : '',
          startDate: (dateSelectionType === 'custom' || dateSelectionType === 'current_month') ? startDate : '',
          endDate: (dateSelectionType === 'custom' || dateSelectionType === 'current_month') ? endDate : '',
          period: 'day',
          showFunnelAndGoals,
          leadGoalSnapshot: showFunnelAndGoals ? leadGoalSnapshot : null
        }
      };
      
      const res = await reportsApi.shareReport(data);
      const url = `${window.location.origin}/shared/${res.data.token}`;
      setShareLink(url);
      toast.success('Link gerado com sucesso!');
    } catch (error) {
      toast.error(error.message || 'Falha ao gerar link');
    } finally {
      setIsSharing(false);
    }
  };

  if (isVisualizing) {
    // Dados das Clínicas — rendered by ReportVisualization right below the KPI
    // header, when the selected account has a clinic-data routine assigned.
    const clinicDbId = selectedAccount && accounts.find(a => {
      const id = a.account_id || a.metaAccountId || (a.id.startsWith('act_') ? a.id.replace('act_', '') : a.id);
      return id === selectedAccount;
    })?.id;
    let clinicSection = null;
    if (clinicDbId) {
      const { start, end } = computeDemographicDates();
      const totalSpend = reportData.reduce((s, r) => s + parseFloat(r.spend || 0), 0);
      clinicSection = <ClinicDataSection adAccountIds={[clinicDbId]} startDate={start} endDate={end} campaignSpend={totalSpend} />;
    }

    return (
      <>
        <ReportVisualization
          data={reportData}
          previousData={previousReportData}
          secondaryData={secondaryReportData}
          level={level}
          conversionType={conversionType}
          onBack={handleBackToOptions}
          onExportCsv={handleExportCsv}
          onShare={() => setIsShareModalOpen(true)}
          demographicsData={demographicsData}
          campaignSummary={campaignSummary}
          clinicSection={clinicSection}
          showFunnelAndGoals={showFunnelAndGoals}
          leadGoal={showFunnelAndGoals ? leadGoal : null}
          leadGoalAchieved={showFunnelAndGoals ? monthAchieved : 0}
          leadGoalLegend={showFunnelAndGoals ? leadGoalLegend : ''}
        />

        {/* Modal de Compartilhamento */}
        {isShareModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
            <div className="bg-surface rounded-2xl p-6 w-full max-w-md animate-fade-in shadow-2xl">
              <h3 className="text-xl font-bold font-headline mb-4 text-on-surface flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">share</span> Compartilhar Relatório
              </h3>
              
              <div className="space-y-4">
                <p className="text-sm text-secondary font-body mb-4">
                  Gere um link público para este relatório. Qualquer pessoa com o link poderá visualizá-lo até a expiração.
                </p>
                
                {!shareLink ? (
                  <>
                    <div>
                      <label className="block text-xs font-bold text-secondary mb-2">Nome do link (opcional)</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="Ex: Relatório Cliente X — Maio 2026"
                        value={shareLabel}
                        onChange={(e) => setShareLabel(e.target.value)}
                        disabled={isSharing}
                        maxLength={255}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 bg-surface-container-low rounded-xl">
                      <div>
                        <p className="text-sm font-bold text-on-surface">Link permanente</p>
                        <p className="text-xs text-secondary">Não expira. Gerencie em Configurações.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSharePermanent(v => !v)}
                        disabled={isSharing}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${sharePermanent ? 'bg-primary' : 'bg-outline-variant'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${sharePermanent ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {!sharePermanent && (
                      <div>
                        <label className="block text-xs font-bold text-secondary mb-2">Expira em</label>
                        <select
                          className="input w-full"
                          value={shareExpires}
                          onChange={(e) => setShareExpires(e.target.value)}
                          disabled={isSharing}
                        >
                          <option value={24}>24 Horas</option>
                          <option value={48}>48 Horas</option>
                          <option value={72}>72 Horas (3 Dias)</option>
                          <option value={168}>1 Semana</option>
                        </select>
                      </div>
                    )}

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        className="px-4 py-2 rounded-full font-bold text-sm text-secondary hover:bg-surface-container"
                        onClick={() => { setIsShareModalOpen(false); setShareLink(''); setShareLabel(''); setSharePermanent(false); }}
                        disabled={isSharing}
                      >
                        Cancelar
                      </button>
                      <button
                        className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold text-sm flex items-center gap-2"
                        onClick={generateShareLink}
                        disabled={isSharing}
                      >
                        {isSharing ? <span className="material-symbols-outlined animate-spin text-sm">refresh</span> : null}
                        {isSharing ? 'Gerando...' : 'Gerar Link'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="p-3 bg-surface-container-low rounded-xl border border-outline-variant break-all flex items-center justify-between gap-2">
                      <span className="text-sm text-on-surface select-all overflow-hidden text-ellipsis">{shareLink}</span>
                      <button 
                        title="Copiar Link"
                        className="text-primary hover:text-primary-focus p-2 rounded-lg hover:bg-primary/10 transition-colors shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(shareLink);
                          toast.success('Link copiado!');
                        }}
                      >
                         <span className="material-symbols-outlined text-[20px]">content_copy</span>
                      </button>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button
                        className="px-6 py-2 rounded-full bg-surface-container font-bold text-sm text-on-surface hover:bg-surface-container-high"
                        onClick={() => {
                          setIsShareModalOpen(false);
                          setShareLink('');
                          setShareLabel('');
                          setSharePermanent(false);
                        }}
                      >
                        Fechar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
      <header className="mb-12">
        <h1 className="text-4xl lg:text-5xl font-extrabold font-headline tracking-tight text-on-surface mb-4">Gerar Novo Relatório</h1>
        <p className="text-secondary max-w-2xl font-body leading-relaxed text-lg">
          Configure a lente do seu relatório de performance. Selecione os parâmetros abaixo para gerar uma visualização curada dos seus dados.
        </p>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* Main Form Section */}
        <div className="xl:col-span-8 bg-surface-container-lowest rounded-xl p-6 lg:p-10 shadow-[0_8px_32px_rgba(205,25,249,0.04)]">
          <div className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Account Selection */}
              <div className="space-y-3">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Conta de Anúncio</label>
                <div className="relative">
                  {/* Selected display / search trigger */}
                  <button
                    type="button"
                    onClick={() => { setAccountDropdownOpen(v => !v); setAccountSearch(''); }}
                    className="w-full bg-surface-container-low px-5 py-4 rounded-xl text-left font-body text-sm flex items-center justify-between focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <span className={selectedAccount ? 'text-on-surface' : 'text-slate-400'}>
                      {selectedAccount
                        ? (() => {
                            const acc = accounts.find(a => {
                              const id = a.account_id || a.metaAccountId || (a.id.startsWith('act_') ? a.id.replace('act_', '') : a.id);
                              return id === selectedAccount;
                            });
                            return acc ? `${acc.name} (${acc.currency})` : selectedAccount;
                          })()
                        : 'Selecione uma conta'}
                    </span>
                    <span className="material-symbols-outlined text-slate-400 text-[20px]">
                      {accountDropdownOpen ? 'expand_less' : 'expand_more'}
                    </span>
                  </button>

                  {/* Dropdown */}
                  {accountDropdownOpen && (
                    <>
                    <div className="fixed inset-0 z-40" onClick={() => setAccountDropdownOpen(false)} />
                    <div className="absolute z-50 top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                      {/* Search input */}
                      <div className="p-2 border-b border-slate-100">
                        <div className="relative">
                          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Buscar conta..."
                            value={accountSearch}
                            onChange={e => setAccountSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                      </div>
                      {/* Options list */}
                      <div className="max-h-52 overflow-y-auto">
                        {accounts
                          .filter(acc => acc.name.toLowerCase().includes(accountSearch.toLowerCase()))
                          .map(acc => {
                            const cleanId = acc.account_id || acc.metaAccountId || (acc.id.startsWith('act_') ? acc.id.replace('act_', '') : acc.id);
                            const isSelected = cleanId === selectedAccount;
                            return (
                              <button
                                key={cleanId}
                                type="button"
                                onClick={() => { setSelectedAccount(cleanId); setAccountDropdownOpen(false); }}
                                className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-slate-50 transition-colors ${isSelected ? 'bg-primary/5 text-primary font-bold' : 'text-slate-700'}`}
                              >
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-primary' : 'bg-slate-200'}`} />
                                <span className="truncate">{acc.name}</span>
                                <span className="ml-auto text-[10px] font-mono text-slate-400 flex-shrink-0">{acc.currency}</span>
                              </button>
                            );
                          })}
                        {accounts.filter(acc => acc.name.toLowerCase().includes(accountSearch.toLowerCase())).length === 0 && (
                          <p className="px-4 py-3 text-sm text-slate-400 text-center">Nenhuma conta encontrada</p>
                        )}
                      </div>
                    </div>
                    </>
                  )}
                </div>
              </div>

              {/* Granularity Level */}
              <div className="space-y-3">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Nível de Granularidade</label>
                <div className="relative group">
                  <select 
                    className="w-full bg-surface-container-low appearance-none px-5 py-4 rounded-xl border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm cursor-pointer"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                  >
                    <option value="geral">Geral (Criativos + Campanhas)</option>
                    <option value="campaign">Nível de Campanha</option>
                    <option value="ad">Nível de Criativo / Anúncio</option>
                  </select>
                  <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">layers</span>
                </div>
              </div>

              {/* Date Selection */}
              <div className="md:col-span-2 space-y-3">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Período</label>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <button
                    type="button"
                    className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'preset' && datePreset === 'last_7d' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                    onClick={() => { setDateSelectionType('preset'); setDatePreset('last_7d'); }}
                  >Últimos 7 Dias</button>
                  <button
                    type="button"
                    className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'preset' && datePreset === 'last_14d' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                    onClick={() => { setDateSelectionType('preset'); setDatePreset('last_14d'); }}
                  >Últimos 14 Dias</button>
                  <button
                    type="button"
                    className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'preset' && datePreset === 'last_30d' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                    onClick={() => { setDateSelectionType('preset'); setDatePreset('last_30d'); }}
                  >Últimos 30 Dias</button>
                  <button
                    type="button"
                    className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'preset' && datePreset === 'lifetime' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                    onClick={() => { setDateSelectionType('preset'); setDatePreset('lifetime'); }}
                  >Vitalício</button>
                  <button
                    type="button"
                    className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'current_month' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                    onClick={() => {
                      const d = new Date();
                      setDateSelectionType('current_month');
                      setStartDate(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]);
                      setEndDate(d.toISOString().split('T')[0]);
                    }}
                  >Mês Atual</button>
                  <button
                    type="button"
                    className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'custom' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                    onClick={() => setDateSelectionType('custom')}
                  >Personalizado</button>
                </div>

                {(dateSelectionType === 'custom' || dateSelectionType === 'current_month') && (
                  <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-surface-container-low rounded-xl border border-outline-variant/30 animate-fade-in">
                    <div>
                      <label className="block text-xs font-bold text-secondary mb-1">Data de Início</label>
                      <div className="relative">
                        <input
                          type="date"
                          className="w-full bg-white appearance-none px-4 py-2.5 rounded-lg border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-secondary mb-1">Data de Fim</label>
                      <div className="relative">
                        <input
                          type="date"
                          className="w-full bg-white appearance-none px-4 py-2.5 rounded-lg border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Visualization Toggle (Modern Card Radio) */}
            <div className="space-y-4">
              <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Objetivo de Conversão (Foco do Relatório)</label>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {conversionOptions.map((conv) => {
                  let icon = 'bar_chart';
                  if (conv.value === 'lead') icon = 'group_add';
                  if (conv.value === 'message') icon = 'chat';
                  if (conv.value === 'click') icon = 'ads_click';
                  if (conv.value === 'conversion') icon = 'trending_up';
                  if (conv.value === 'purchase') icon = 'shopping_cart';

                  const isActive = conversionType === conv.value;

                  return (
                    <div 
                      key={conv.value}
                      onClick={() => setConversionType(conv.value)}
                      className={`p-4 rounded-xl flex flex-col items-center gap-3 cursor-pointer transition-all ${isActive ? 'border-2 border-primary bg-primary/5' : 'border border-outline-variant bg-white hover:border-primary/40 shadow-sm'}`}
                    >
                      <span className={`material-symbols-outlined text-3xl ${isActive ? 'text-primary' : 'text-slate-400'}`}>{icon}</span>
                      <span className={`font-headline text-xs text-center leading-tight font-bold ${isActive ? 'text-on-surface' : 'text-slate-600'}`}>{conv.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Meta e Funil Planejado Toggle */}
            <div className="flex items-center gap-3 p-4 bg-surface-container-low rounded-xl border border-outline-variant/30">
              <input
                type="checkbox"
                id="showFunnelAndGoals"
                checked={showFunnelAndGoals}
                onChange={(e) => setShowFunnelAndGoals(e.target.checked)}
                className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
              />
              <label htmlFor="showFunnelAndGoals" className="text-sm font-bold text-on-surface cursor-pointer select-none">
                Exibir dados de meta e funil planejado
              </label>
            </div>

            <div className="pt-6">
              <button 
                type="button" 
                onClick={handleGenerateReport}
                disabled={isLoading}
                className="w-full py-5 lg:py-6 bg-gradient-to-r from-primary to-primary-container text-white rounded-full font-headline text-lg lg:text-xl font-extrabold shadow-[0_12px_40px_-12px_rgba(205,25,249,0.4)] hover:scale-[1.01] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:hover:scale-100"
              >
                {isLoading ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">refresh</span>
                    <span>Processando...</span>
                  </>
                ) : (
                  <>
                    <span>Visualizar Relatório</span>
                    <span className="material-symbols-outlined">trending_up</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Side Sidebar: Advanced Insights */}
        <div className="xl:col-span-4 space-y-8">
          <section className="bg-surface-container-low rounded-xl p-8 relative overflow-hidden group border border-outline-variant/30">
            <div className="relative z-10">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-6 shadow-sm">
                <span className="material-symbols-outlined text-primary">auto_awesome</span>
              </div>
              <h3 className="font-headline text-xl font-bold mb-4">Dicas Avançadas</h3>
              <div className="space-y-4">
                <div className="p-4 bg-white/50 backdrop-blur rounded-xl border border-white">
                  <p className="text-[10px] font-bold uppercase text-primary mb-1">Análise de Campanha</p>
                  <p className="text-xs text-secondary leading-relaxed font-body">
                    Agrega métricas por objetivo. Ideal para acompanhar ROAS geral, Custo por Lead (CPL) e métricas de funil superiores.
                  </p>
                </div>
                <div className="p-4 bg-white/50 backdrop-blur rounded-xl border border-white">
                  <p className="text-[10px] font-bold uppercase text-primary mb-1">Análise de Criativos</p>
                  <p className="text-xs text-secondary leading-relaxed font-body">
                    Visão granular de anúncios específicos. Use para testes A/B e identificar as melhores mídias através das miniaturas de criativos.
                  </p>
                </div>
              </div>
            </div>
            {/* Abstract decorative element */}
            <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors"></div>
          </section>

          <section className="p-6 bg-surface-container-low rounded-xl border-l-4 border-primary/50 relative overflow-hidden">
             <div className="relative z-10">
                <h4 className="font-headline text-sm font-bold mb-3 flex items-center gap-2">
                   <span className="material-symbols-outlined text-sm text-primary">lightbulb</span> Dica Rápida
                </h4>
                <p className="text-xs text-secondary leading-relaxed font-body">
                    Gerar relatórios com o período de <strong>Últimos 7 Dias</strong> geralmente resulta em tempos de carregamento 40% mais rápidos e visões mais acionáveis para curto prazo.
                </p>
             </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Reports;
